<script>
  /**
   * Rules for all properties — facts and policies the manager states once, for every property.
   *
   * plans/STRAO_DRAFT_QUALITY_PLAN.md, T1. Retrieved per guest message like property
   * information; a property's own information wins when the two disagree. Distinct from saved
   * replies (Situations.svelte): a saved reply is HOW you answer, a rule is a FACT the draft can
   * draw on. The page says so, because the two are easy to confuse.
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import DataTable from '../components/DataTable.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';

  let loading = $state(true);
  let error = $state(null);
  let items = $state([]);

  let editTarget = $state(null);      // the rule being edited, or 'new'
  let form = $state(null);
  let saving = $state(false);
  let confirmTarget = $state(null);

  // Same pattern as Situations.svelte: the server refuses to guess a super-user's tenant.
  const tenantId = $derived(authStore.tenantId ?? undefined);

  const canSave = $derived(
    !!form && form.title.trim().length > 0 && form.content.trim().length >= 10
  );

  async function load() {
    loading = true;
    error = null;
    try {
      items = await api.listRules({ tenant_id: tenantId });
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { tenantId; load(); });

  function openNew() {
    editTarget = 'new';
    form = { title: '', content: '', is_active: true };
  }

  function openEdit(rule) {
    editTarget = rule;
    form = { title: rule.title, content: rule.content, is_active: rule.is_active };
  }

  function closeEditor() {
    editTarget = null;
    form = null;
  }

  function onEditorKey(e) {
    if (e.key === 'Escape' && !confirmTarget) closeEditor();
  }

  async function save() {
    saving = true;
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content.trim(),
        is_active: form.is_active,
      };
      if (editTarget === 'new') {
        await api.createRule(payload, { tenant_id: tenantId });
        toast.success(`Saved "${payload.title}"`);
      } else {
        await api.updateRule(editTarget.id, payload, { tenant_id: tenantId });
        toast.success(`Updated "${payload.title}"`);
      }
      closeEditor();
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      saving = false;
    }
  }

  async function remove() {
    const target = confirmTarget;
    confirmTarget = null;
    try {
      await api.deleteRule(target.id, { tenant_id: tenantId });
      toast.success(`Deleted "${target.title}"`);
      if (editTarget && editTarget !== 'new' && editTarget.id === target.id) closeEditor();
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  }

  const truncate = (s, n = 90) => (s.length > n ? `${s.slice(0, n)}…` : s);

  const columns = [
    { key: 'title', label: 'Rule' },
    { key: 'content', label: 'What it says', render: (r) => truncate(r.content) },
    { key: 'is_active', label: 'Active', render: (r) => (r.is_active ? 'Yes' : 'No') },
    { key: 'is_searchable', label: 'In use',
      render: (r) => (!r.is_active ? '—' : r.is_searchable ? 'Yes' : 'Pending') },
  ];
</script>

<PageHeader
  title="Rules for all properties"
  subtitle="Facts and policies that are true at every property you manage. Drafts use them when they're relevant; a property's own information wins if the two disagree.">
  {#snippet children()}
    <button type="button" class="p-btn p-btn-primary" onclick={openNew}>
      <i class="fas fa-plus" aria-hidden="true"></i> New rule
    </button>
  {/snippet}
</PageHeader>

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if !loading && !items.length}
  <EmptyState icon="fa-scale-balanced" title="No rules yet"
              message="Write down anything that holds for every property — how door codes are formed,
                       your late-checkout policy, how a visitor gets added to a gate list. To set how
                       you answer a recurring request, use Saved replies instead." />
{:else}
  <DataTable {columns} rows={items} {loading}
             emptyMessage="No rules yet."
             onRowClick={openEdit} />
  <p class="mt-2 text-xs p-faint">
    Select a rule to edit it. "Pending" means it is saved but not searchable yet; that resolves on
    its own within a few hours.
  </p>
{/if}

<svelte:window on:keydown={form ? onEditorKey : undefined} />

{#if form}
  <!-- svelte-ignore a11y_click_events_have_key_events -->
  <div class="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
       role="presentation"
       onclick={(e) => { if (e.target === e.currentTarget) closeEditor(); }}>
    <div class="p-card max-h-[90vh] w-full max-w-2xl overflow-y-auto p-5" role="dialog"
         aria-modal="true" aria-label="Edit rule" tabindex="-1">
      <h2 class="text-sm font-semibold">{editTarget === 'new' ? 'New rule' : 'Edit rule'}</h2>

      <div class="mt-4 space-y-4">
        <div>
          <label for="rule-title" class="text-xs font-medium p-muted">What is it about?</label>
          <input id="rule-title" class="p-input mt-1" type="text" bind:value={form.title}
                 placeholder="Door codes" />
        </div>

        <div>
          <label for="rule-content" class="text-xs font-medium p-muted">The rule</label>
          <textarea id="rule-content" class="p-input mt-1" rows="4" bind:value={form.content}
                    placeholder="Every door code is the last four digits of the guest's phone number."></textarea>
          <p class="mt-1 text-xs p-faint">
            State the fact or policy itself, the way you'd explain it to a new assistant. This is
            information the draft can use, not wording it will copy — for that, use Saved replies.
          </p>
        </div>

        <label class="flex items-center gap-2 text-sm">
          <input type="checkbox" bind:checked={form.is_active} />
          <span>Active</span>
        </label>
      </div>

      <div class="mt-5 flex items-center justify-between gap-2">
        {#if editTarget !== 'new'}
          <button type="button" class="p-btn p-btn-quiet px-2 py-1 text-xs"
                  style="color: var(--p-danger)"
                  onclick={() => (confirmTarget = editTarget)}>Delete</button>
        {:else}<span></span>{/if}
        <div class="flex gap-2">
          <button type="button" class="p-btn p-btn-quiet" onclick={closeEditor}>Cancel</button>
          <button type="button" class="p-btn p-btn-primary" disabled={saving || !canSave}
                  onclick={save}>Save</button>
        </div>
      </div>
      {#if !canSave}
        <p class="mt-2 text-right text-xs p-faint">Needs a title and at least 10 characters of rule.</p>
      {/if}
    </div>
  </div>
{/if}

<ConfirmModal open={!!confirmTarget}
              title="Delete this rule?"
              message={confirmTarget
                ? `"${confirmTarget.title}" will no longer be used in drafts. To pause it instead, `
                  + `turn off Active.`
                : ''}
              confirmLabel="Delete"
              onConfirm={remove}
              onCancel={() => (confirmTarget = null)} />
